/**
 * NewLocationModal — Phase 26 / Task 26.5 (DEC-147 #3 / #5 / #10).
 *
 * Renamed from `NewTankBatteryModal` when the Management console retired
 * (DEC-147 #10). "Sites & Operators" is now the sole hauler-catalog console
 * and this modal creates ANY typed hauler-location — Tank Battery, Rig,
 * Compressor Station, Other — via a single `POST /v1/tank-batteries` (route
 * name kept for API contract stability; the model behind it was renamed to
 * `hauler_locations` in Task 26.1).
 *
 * Fields:
 *   - Name (uppercased server-side per DEC-147 #9; a mixed-case input round-
 *     trips as UPPERCASE).
 *   - Site Type (default Tank Battery). Sourced from tenant-filtered
 *     `GET /v1/site-types` — the picker only lists types the tenant has
 *     enabled in `tenant_site_type_settings`.
 *   - Operator (from active hauler_operators — same as pre-26.5 flow).
 *   - County + State (optional, DEC-310). A typed county is stored UPPERCASE with
 *     source USER; leave blank to have it filled from the site's GPS pin (OK, TX).
 *     State is required once a county is typed (the table has no state column).
 *   - Pickup / Dropoff capability toggles. Rendered for EVERY hauler-created
 *     site type. Server defaults (`can_pickup=true, can_dropoff=false`) match
 *     the DEC-147 #5 default; the toggles let the dispatcher override at
 *     creation (e.g. a Rig used for water dropoff flips can_dropoff=true).
 *   - Private site checkbox (existing).
 *   - **Wells section rendered ONLY when Site Type = TANK_BATTERY** — mirrors
 *     the DEC-147 #4 "well-linking is TANK_BATTERY-exclusive" rule. In this
 *     modal the section is a placeholder that says "Link wells after creating
 *     this location"; the actual link flow already lives on the detail screen
 *     (SearchWellModal / LinkWellModal), invoked from the tanks list.
 */
import {
  lockedExpressCustomerId,
  onlyExpressCustomers,
  useExpressCustomerState,
} from '../../lib/express-customer';
import * as React from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { Combobox } from '../../components/Combobox';
import { fieldStyle, labelStyle, FormRow } from './form-bits';
import { useCreateTankBattery, useSiteTypes, type HaulerOperator, type SiteType } from './hooks';

export interface NewLocationModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly operators: readonly HaulerOperator[];
}

const DEFAULT_SITE_TYPE_CODE = 'TANK_BATTERY';

/** Human-readable defaults matching the DB column defaults per DEC-147 #5. */
const DEFAULT_CAN_PICKUP = true;
const DEFAULT_CAN_DROPOFF = false;

export function NewLocationModal({
  open,
  onOpenChange,
  operators,
}: NewLocationModalProps): React.JSX.Element {
  const [name, setName] = React.useState('');
  const [operatorId, setOperatorId] = React.useState('');
  const { express, pending: expressPending } = useExpressCustomerState();
  const lockedOperatorId = lockedExpressCustomerId(express);
  React.useEffect(() => {
    if (lockedOperatorId !== null && operatorId === '') setOperatorId(lockedOperatorId);
  }, [lockedOperatorId, operatorId]);
  const [siteTypeId, setSiteTypeId] = React.useState('');
  const [canPickup, setCanPickup] = React.useState<boolean>(DEFAULT_CAN_PICKUP);
  const [canDropoff, setCanDropoff] = React.useState<boolean>(DEFAULT_CAN_DROPOFF);
  const [isPrivate, setIsPrivate] = React.useState(false);
  const [county, setCounty] = React.useState('');
  const [countyState, setCountyState] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const siteTypesQuery = useSiteTypes();
  // Memoize so the useEffect deps below stay stable across renders (a raw
  // `?? []` returns a new array reference every render → effect thrashes).
  const siteTypes = React.useMemo<readonly SiteType[]>(
    () => siteTypesQuery.data?.siteTypes ?? [],
    [siteTypesQuery.data],
  );

  // Reset when the modal closes; on re-open we start from a clean slate so
  // stale drafts never leak between two sequential creations.
  React.useEffect(() => {
    if (open) return;
    setName('');
    setOperatorId('');
    setSiteTypeId('');
    setCanPickup(DEFAULT_CAN_PICKUP);
    setCanDropoff(DEFAULT_CAN_DROPOFF);
    setIsPrivate(false);
    setCounty('');
    setCountyState('');
    setError(null);
  }, [open]);

  // Resolve the default site type id once the catalog lands. The picker defaults
  // to TANK_BATTERY per DEC-147 #10; if a tenant has disabled TANK_BATTERY (edge
  // case) the first enabled type wins so the form is never left unpicked. Runs
  // after the reset effect above so an open→close→open cycle repicks the default.
  React.useEffect(() => {
    if (!open) return;
    if (siteTypeId !== '') return;
    if (siteTypes.length === 0) return;
    const preferred = siteTypes.find((st) => st.code === DEFAULT_SITE_TYPE_CODE);
    setSiteTypeId((preferred ?? siteTypes[0]!).id);
  }, [open, siteTypes, siteTypeId]);

  const create = useCreateTankBattery(() => {
    toast.success('Location created');
    onOpenChange(false);
  });

  // §58 PR 4 — on EXPRESS, sites only for the one customer.
  const operatorOptions = onlyExpressCustomers(
    operators
      .filter((o) => o.isActive)
      .map((o) => ({ value: o.id, label: `${o.resolvedName} (${o.ticketAbbreviation})` })),
    express,
  );

  const siteTypeOptions = siteTypes.map((st) => ({ value: st.id, label: st.label }));
  const currentSiteType = siteTypes.find((st) => st.id === siteTypeId) ?? null;
  const isTankBattery = currentSiteType?.code === DEFAULT_SITE_TYPE_CODE;

  const handleSave = (): void => {
    if (name.trim() === '') {
      setError('Name is required.');
      return;
    }
    if (operatorId === '') {
      setError('Pick an operator.');
      return;
    }
    if (siteTypeId === '') {
      setError('Pick a site type.');
      return;
    }
    const typedCounty = county.trim();
    const typedState = countyState.trim();
    if (typedCounty !== '' && !/^[A-Za-z]{2}$/.test(typedState)) {
      setError("Pick the county's state (two letters).");
      return;
    }
    setError(null);
    create.mutate(
      {
        name: name.trim(),
        haulerOperatorId: operatorId,
        isPrivate,
        siteTypeId,
        // Send explicit booleans so the server persists the toggle even when
        // the dispatcher leaves the defaults in place (audit trail is honest
        // about "the dispatcher chose these values", not "the DB defaulted").
        canPickup,
        canDropoff,
        // DEC-310 — only when typed; the server uppercases it and stores source USER.
        ...(typedCounty !== '' && { county: typedCounty, countyState: typedState.toUpperCase() }),
      },
      { onError: () => setError('Could not create the location. Try again.') },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New location</DialogTitle>
        </DialogHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <FormRow label="Name" htmlFor="loc-name" required>
            <input
              id="loc-name"
              value={name}
              onChange={(e): void => setName(e.target.value)}
              placeholder="e.g. Maxine Battery"
              style={fieldStyle}
            />
          </FormRow>
          <FormRow label="Site type" htmlFor="loc-site-type" required>
            <Combobox
              id="loc-site-type"
              options={siteTypeOptions}
              value={siteTypeId}
              onChange={setSiteTypeId}
              placeholder="Select site type…"
              searchPlaceholder="Search site types…"
              emptyText="No site types available."
            />
          </FormRow>
          <FormRow label="Operator" htmlFor="loc-operator" required>
            <Combobox
              id="loc-operator"
              options={operatorOptions}
              value={operatorId}
              onChange={setOperatorId}
              disabled={lockedOperatorId !== null || expressPending}
              placeholder="Select operator…"
              searchPlaceholder="Search operators…"
              emptyText="No operators — add one first."
            />
          </FormRow>
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ flex: 1 }}>
              <FormRow label="County" htmlFor="loc-county">
                <input
                  id="loc-county"
                  value={county}
                  maxLength={60}
                  onChange={(e): void => setCounty(e.target.value)}
                  style={{ ...fieldStyle, textTransform: 'uppercase' }}
                />
              </FormRow>
            </div>
            <div style={{ width: '80px' }}>
              <FormRow label="State" htmlFor="loc-county-state">
                <input
                  id="loc-county-state"
                  value={countyState}
                  maxLength={2}
                  onChange={(e): void => setCountyState(e.target.value)}
                  placeholder="ST"
                  style={{ ...fieldStyle, textTransform: 'uppercase' }}
                />
              </FormRow>
            </div>
          </div>
          <p style={{ margin: 0, fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
            Leave blank to fill it from the site&apos;s GPS pin (OK and TX).
          </p>
          {/* DEC-147 #5 — capability toggles surface for EVERY hauler-created type.
              A Rig used for water delivery flips can_dropoff=true here; the
              default (pickup=true, dropoff=false) matches the DB defaults. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={labelStyle}>Capability</span>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={canPickup}
                onChange={(e): void => setCanPickup(e.target.checked)}
              />
              <span style={{ ...labelStyle, marginBottom: 0, textTransform: 'none' }}>Pickup</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={canDropoff}
                onChange={(e): void => setCanDropoff(e.target.checked)}
              />
              <span style={{ ...labelStyle, marginBottom: 0, textTransform: 'none' }}>Dropoff</span>
            </label>
          </div>
          {/* DEC-147 #4 — well-linking is TANK_BATTERY-exclusive. Wells section
              renders only when the picker selects TANK_BATTERY; other types
              never show a Wells affordance (Compressor Station, Rig, Other). */}
          {isTankBattery && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <span style={labelStyle}>Wells</span>
              <p
                data-testid="wells-hint"
                style={{
                  margin: 0,
                  fontSize: '12px',
                  color: 'var(--color-text-tertiary)',
                  lineHeight: 1.5,
                }}
              >
                Link wells to this site after creating it — the detail screen has a &ldquo;Link
                well&rdquo; action once the location exists.
              </p>
            </div>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={isPrivate}
              onChange={(e): void => setIsPrivate(e.target.checked)}
            />
            <span style={{ ...labelStyle, marginBottom: 0, textTransform: 'none' }}>
              Private site (unlisted — no platform well connection)
            </span>
          </label>
          {error !== null && (
            <p role="alert" style={{ ...labelStyle, color: 'var(--color-danger)' }}>
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={(): void => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={create.isPending}
            onClick={handleSave}
            style={{
              backgroundColor: 'var(--color-accent-blue)',
              color: 'var(--color-on-accent)',
              borderColor: 'var(--color-accent-blue)',
            }}
          >
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
